import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CodeBlockProps {
  /** The literal text copied to the clipboard and shown verbatim. */
  code: string;
  /** Optional label shown above the block (e.g. a filename). */
  title?: string;
  /** Constrain height and scroll for long multi-line snippets. */
  maxHeight?: string;
  className?: string;
}

/**
 * Monospaced, copy-to-clipboard code block. Single source of truth for the
 * copy affordance (there was none before — copy was hand-rolled inline in a
 * couple of places).
 */
export function CodeBlock({ code, title, maxHeight, className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); }, 1500);
    });
  };

  return (
    <div className={cn('rounded-md border border-border-subtle bg-muted/40', className)}>
      {title ? (
        <div className="flex items-center justify-between border-b border-border-subtle px-3 py-1.5">
          <span className="font-mono text-xs text-muted-foreground">{title}</span>
        </div>
      ) : null}
      <div className="relative">
        <button
          type="button"
          onClick={copy}
          aria-label="Copy to clipboard"
          className="absolute right-2 top-2 inline-flex items-center gap-1 rounded border border-border-subtle bg-background/80 px-2 py-1 text-xs text-muted-foreground hover:bg-accent/60"
        >
          {copied ? <Check className="h-3 w-3 text-sev-5" /> : <Copy className="h-3 w-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
        <pre
          className="overflow-auto px-3 py-2.5 pr-16 text-xs leading-relaxed"
          style={maxHeight ? { maxHeight } : undefined}
        >
          <code className="font-mono">{code}</code>
        </pre>
      </div>
    </div>
  );
}
